import React, { useMemo, useState } from 'react';
import { getWhatsAppBookingLink } from '../config/contactConfig';

const DEFAULT_TIME_SLOTS = [
  '06:00', '07:00', '08:00', '09:00', '10:00', '11:00',
  '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'
];

const formatPrice = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;

const QuickBookingModal = ({ isOpen, onClose, bookingData, onContinue }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [specialRequest, setSpecialRequest] = useState('');
  const [errors, setErrors] = useState({});

  const serviceTitle = bookingData?.service?.title || bookingData?.service?.name || 'Service Booking';
  const serviceIcon = bookingData?.service?.image || bookingData?.service?.icon || '🕉️';

  const pricing = useMemo(() => {
    const model = bookingData?.pricing || {};
    return {
      baseLabel: model.baseLabel || 'Base Charges',
      baseAmount: Number(model.dakshina || bookingData?.package?.price || 0),
      platformFee: Number(model.platformFee || 0),
      total: Number(bookingData?.totalPrice || model.total || 0)
    };
  }, [bookingData]);

  const resetAndClose = () => {
    setSelectedDate('');
    setSelectedTime('');
    setSpecialRequest('');
    setErrors({});
    onClose();
  };

  const handleContinue = () => {
    const nextErrors = {};
    if (!selectedDate) nextErrors.date = 'Please select a preferred date';
    if (!selectedTime) nextErrors.time = 'Please select a preferred time';

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    onContinue({
      ...bookingData,
      date: selectedDate,
      time: selectedTime,
      specialRequest,
      preBooking: {
        date: selectedDate,
        time: selectedTime,
        specialRequest
      }
    });
    resetAndClose();
  };

  const handleWhatsAppBooking = () => {
    const selection = [selectedDate, selectedTime].filter(Boolean).join(' at ');
    const serviceWithSelection = selection
      ? `${serviceTitle} (${selection})`
      : serviceTitle;
    window.open(getWhatsAppBookingLink(serviceWithSelection), '_blank');
  };

  if (!isOpen || !bookingData) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={resetAndClose}></div>

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="text-3xl">{serviceIcon}</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Quick Pre-Booking</h3>
                  <p className="text-sm text-gray-600">{serviceTitle}</p>
                </div>
              </div>
              <button onClick={resetAndClose} className="text-2xl text-gray-400 hover:text-gray-700">×</button>
            </div>
          </div>

          <div className="grid gap-6 p-6 md:grid-cols-2">
            <div>
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700">Pricing Summary</h4>
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
                <div className="mb-2 flex items-center justify-between text-sm text-gray-700">
                  <span>{pricing.baseLabel}</span>
                  <span className="font-medium">{formatPrice(pricing.baseAmount)}</span>
                </div>
                <div className="mb-3 flex items-center justify-between text-sm text-gray-700">
                  <span>Platform Fee</span>
                  <span className="font-medium">{formatPrice(pricing.platformFee)}</span>
                </div>
                <div className="border-t border-rose-200 pt-3">
                  <div className="flex items-center justify-between text-base font-bold text-gray-900">
                    <span>Total</span>
                    <span className="text-rose-600">{formatPrice(pricing.total)}</span>
                  </div>
                </div>
              </div>
              <p className="mt-3 text-xs text-gray-500">
                Final confirmation and full details will be completed on the booking page.
              </p>
            </div>

            <div>
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700">Preferred Schedule</h4>
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Preferred Date *</label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(event) => {
                      setSelectedDate(event.target.value);
                      if (errors.date) setErrors((prev) => ({ ...prev, date: '' }));
                    }}
                    min={new Date().toISOString().split('T')[0]}
                    className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 ${errors.date ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {errors.date && <p className="mt-1 text-xs text-red-600">{errors.date}</p>}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Preferred Time *</label>
                  <select
                    value={selectedTime}
                    onChange={(event) => {
                      setSelectedTime(event.target.value);
                      if (errors.time) setErrors((prev) => ({ ...prev, time: '' }));
                    }}
                    className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 ${errors.time ? 'border-red-500' : 'border-gray-300'}`}
                  >
                    <option value="">Select a time slot</option>
                    {DEFAULT_TIME_SLOTS.map((slot) => (
                      <option key={slot} value={slot}>{slot}</option>
                    ))}
                  </select>
                  {errors.time && <p className="mt-1 text-xs text-red-600">{errors.time}</p>}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Special Request (optional)</label>
                  <textarea
                    value={specialRequest}
                    onChange={(event) => setSpecialRequest(event.target.value)}
                    rows={3}
                    maxLength={250}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
                    placeholder="Any specific requirement for the pandit or ritual setup"
                  />
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-3">
                <div className="flex items-start gap-2">
                  <span className="text-green-600">🛡️</span>
                  <div className="text-sm">
                    <div className="font-medium text-green-800">100% Service Guarantee</div>
                    <div className="text-xs text-green-700">Professional service with traditional authenticity</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-gray-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-end">
            <button
              onClick={handleWhatsAppBooking}
              className="rounded-lg bg-green-500 px-4 py-2 text-sm font-semibold text-white hover:bg-green-600"
            >
              Book via WhatsApp
            </button>
            <button
              onClick={resetAndClose}
              className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleContinue}
              className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700"
            >
              Continue to Booking - {formatPrice(pricing.total)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickBookingModal;
